/**
 * Maps-link parsing helpers — shared between RoutePlanning, Customers list
 * and the future bulk-validate UI.
 *
 * Returning a structured result instead of a plain tuple lets callers
 * explain WHY a customer can/can't be routed:
 *
 *   "ok"            → link parses to valid lat/lng OR lat/lng already set
 *   "missing_link"  → no link AND no coords AND no province (totally blind)
 *   "broken_link"   → link is set but doesn't contain parseable lat/lng
 *                     AND we don't have province/canton fallback either
 *   "approximate"   → no link/coords but we have province/canton, so the
 *                     CR geo catalog will provide an approximate location
 */
export const MAPS_LINK_PATTERNS = [
  /@(-?\d+\.\d+),(-?\d+\.\d+)/,
  /[?&](?:q|query|ll|destination)=(-?\d+\.\d+),(-?\d+\.\d+)/,
  /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
  /^\s*(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)\s*$/,
];

export function extractLatLng(link) {
  if (!link) return null;
  for (const rx of MAPS_LINK_PATTERNS) {
    const m = link.match(rx);
    if (m) {
      const lat = parseFloat(m[1]);
      const lng = parseFloat(m[2]);
      if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) + Math.abs(lng) > 0.0001) {
        return { lat, lng };
      }
    }
  }
  return null;
}

/**
 * Diagnose the routing readiness of a customer.
 *
 * @param {object} c   Customer doc { maps_link, lat, lng, provincia, canton, distrito }
 * @returns {{ status: "ok"|"approximate"|"broken_link"|"missing_link", reason?: string }}
 */
export function diagnoseCustomerRouting(c = {}) {
  const hasCoords = Number.isFinite(c.lat) && Number.isFinite(c.lng) && (Math.abs(c.lat) + Math.abs(c.lng) > 0.0001);
  if (hasCoords) return { status: "ok" };

  const link = (c.maps_link || "").trim();
  if (link) {
    const parsed = extractLatLng(link);
    if (parsed) return { status: "ok" };
    // Link exists but no coords → broken. Best message tells them how to fix.
    return {
      status: "broken_link",
      reason: "El link de Google Maps no contiene coordenadas. Abrí Maps, copiá un link 'compartir → enlace' y pegalo, o ingresá lat/lng manualmente.",
    };
  }

  // No link, no coords. Province-level fallback?
  const hasGeo = Boolean((c.provincia || "").trim() && (c.canton || "").trim());
  if (hasGeo) {
    return {
      status: "approximate",
      reason: "Sin coordenadas; las rutas usarán el cantón/distrito como aproximación.",
    };
  }

  return {
    status: "missing_link",
    reason: "Falta el link de Google Maps, las coordenadas y la provincia/cantón. Este cliente no puede entrar en rutas programadas.",
  };
}
